// English source strings for module 10: vehicles, parking, gate review, ANPR and observed vehicle
// movements (vehicles stream, PD-160 – PD-179).
export const enVehicles = {
  // ------------------------------------------------------------ API messages
  'api.associationEnded': 'This association has already ended.',
  'api.barrierAlreadyRequested': 'A barrier command was already requested for this decision. Review again for a new one.',
  'api.barrierNeedsPermit': 'A barrier command can follow only a permitted-entry decision.',
  'api.barrierNotConfigured': 'No barrier adapter is configured for this site.',
  'api.barrierPermitExpired': 'The permitted-entry decision is too old. Review the current facts again.',
  'api.commandTransition': 'A barrier command cannot go from {from} to {to}.',
  'api.exceptionResolved': 'This plate-read exception is already closed.',
  'api.gateBlocked': 'Entry cannot be permitted. Resolve the listed blockers, deny with a reason or escalate.',
  'api.gateEvaluationExpired': 'This review is too old to act on. The current facts are shown — review them again.',
  'api.gateUnresolved': 'Some facts are unknown or stale, so entry cannot be permitted. Resolve them or escalate.',
  'api.movementAlreadyVoided': 'This movement has already been corrected.',
  'api.movementNotVoidable': 'A correction cannot itself be corrected.',
  'api.noParking': 'This vehicle has no active parking allocation.',
  'api.parkingFull': 'That parking zone is full for this visit window. Choose another zone.',
  'api.parkingNeedsSchedule': 'Parking can be allocated once the visit has start and end times.',
  'api.parkingSpaceTaken': 'That space was just allocated to another vehicle. Choose another space.',
  'api.photoIdTaken': 'A different photo was already saved under this capture. Take the photo again.',
  'api.photoNotPermitted': 'This site’s policy does not allow a photo for this purpose.',
  'api.vehicleAlreadyExpected': 'This vehicle is already expected for this visit.',
  'api.vehicleAlreadyOnsite': 'This vehicle is already recorded as onsite. Record its exit first, or correct the earlier entry.',
  'api.vehicleNotOnsite': 'This vehicle is already recorded as having left.',
  'api.vehicleRemoved': 'This vehicle is no longer expected for the visit.',
  'api.zoneCapacityBelowAllocations': { one: 'One future allocation uses this zone. Reallocate it before lowering the capacity.', other: '{count} future allocations use this zone. Reallocate them before lowering the capacity.' },
  'v.accessibleOverCapacity': 'Accessible places cannot exceed the zone capacity.',
  'v.candidateUnknown': 'Choose one of the listed candidates or enter the corrected plate.',
  'v.driverUnknown': 'Choose a visitor on this visit.',
  'v.exactSpacesOff': 'This site allocates parking by zone only.',
  'v.exitBeforeEntry': 'The exit cannot be before the recorded entry.',
  'v.jurisdiction': 'Use a country code, optionally with a region (for example AE-DU), or XX.',
  'v.jurisdictionNotAllowed': 'This jurisdiction is not in the site’s list.',
  'v.observedTooOld': 'Observations older than 24 hours cannot be recorded here.',
  'v.parkingCode': 'Use 1–20 letters, digits, dots, underscores or hyphens.',
  'v.parkingCodeTaken': 'This code is already used.',
  'v.photoNotice': 'Confirm that the purpose notice was given.',
  'v.photoPurposeRequired': 'Choose at least one photo purpose.',
  'v.plateCharacters': 'Use letters and digits only (separators are ignored).',
  'v.plateTaken': 'This plate is already registered in this jurisdiction at this site.',
  'v.plateTooLong': 'A plate has at most 12 letters and digits.',
  'v.procedureTextRequired': 'Describe the approved offline procedure.',
  'v.simulatorNotInProduction': 'Simulators cannot be used in a production installation.',
  'v.spaceUnavailable': 'Choose an active space in this zone.',
  'v.statusReasonRequired': 'Give a reason.',
  'v.vehicleRequired': 'Choose a vehicle or enter its plate and jurisdiction.',
  'v.zoneUnavailable': 'Choose an active zone of this site.',
  'specialist.barrier-operator': 'Barrier operator',
  'specialist.security-supervisor': 'Security supervisor',

  // ------------------------------------------------------------ vehicles & registry
  'veh.title': 'Vehicles & gate review',
  'veh.intro': 'A plate match locates a candidate. It never opens a gate.',
  'veh.notEntitled': 'Vehicle, parking and plate features are not enabled for this organisation. Gate review of people still works.',
  'veh.tab.queue': 'Gate queue',
  'veh.tab.parking': 'Parking',
  'veh.tab.offline': 'Offline procedure',
  'veh.tab.registry': 'Registry',
  'veh.tab.settings': 'Gate settings',
  'veh.register': 'Register vehicle',
  'veh.registerSub': 'A registered plate helps find a vehicle. It never grants entry.',
  'veh.registerSubmit': 'Register',
  'veh.registry': 'Vehicle registry',
  'veh.registrySub': 'Vehicles known at this site. The same plate in two jurisdictions is two vehicles.',
  'veh.registrySearch': 'Plate',
  'veh.registryNone': 'No vehicles registered.',
  'veh.plate': 'Plate',
  'veh.plateHint': 'As written on the plate. Spaces and dashes are ignored for matching.',
  'veh.jurisdiction': 'Jurisdiction',
  'veh.jurisdictionHint': 'Emirate or country that issued the plate.',
  'veh.make': 'Make',
  'veh.model': 'Model',
  'veh.colour': 'Colour',
  'veh.makeModel': 'Make / model',
  'veh.owners': 'Linked people and companies',
  'veh.status': 'Status',
  'veh.block': 'Block',
  'veh.unblock': 'Unblock',
  'veh.until': 'until {date}',
  'veh.relation.driver': 'Driver',
  'veh.relation.owner': 'Owner',
  'veh.relation.company_vehicle': 'Company vehicle',
  'veh.relation.other': 'Other',
  'veh.driver': 'Driver',
  'veh.driverNone': 'No driver named',
  'veh.drivenBy': 'Driven by {name}',
  'veh.noDetails': 'No vehicle details',
  'veh.parkingRequested': 'Parking needed',
  'veh.accessibleParking': 'Accessible parking needed',
  'veh.parkingIn': 'Parking: {zone}',
  'veh.parkingRequestedNone': 'Parking requested, not yet allocated',
  'veh.visit.title': 'Vehicles',
  'veh.visit.sub': 'Expected vehicles for this visit. A plate is matched only within the visit’s own times.',
  'veh.visit.add': 'Add vehicle',
  'veh.visit.addSubmit': 'Add vehicle',
  'veh.visit.remove': 'Remove',
  'veh.visit.none': 'No vehicle expected.',
  'veh.visit.note': 'Vehicle entry and exit are recorded at the gate, separately from each visitor’s check-in.',
  'vehicle.status.active': 'Active',
  'vehicle.status.blocked': 'Blocked',
  'vehicle.status.inactive': 'Inactive',
  'vpresence.no_observation': 'Vehicle not observed',
  'vpresence.observed_onsite': 'Vehicle onsite',
  'vpresence.observed_departed': 'Vehicle left',
  'vpresence.unknown_conflicting': 'Vehicle presence unknown',

  // ------------------------------------------------------------ parking
  'park.title': 'Parking plan',
  'park.sub': 'Zones, places and current allocations. No gate system is connected.',
  'park.none': 'No parking zones yet.',
  'park.zone': 'Zone',
  'park.space': 'Space',
  'park.spaces': 'Spaces',
  'park.capacity': 'Capacity',
  'park.free': 'Free now',
  'park.freeCount': { zero: 'Full', one: '{count} free', other: '{count} free' },
  'park.taken': 'taken',
  'park.gate': 'Gate',
  'park.code': 'Code',
  'park.name': 'Name',
  'park.directions': 'Directions',
  'park.directionsHint': 'Shown to the visitor on their pass.',
  'park.addZone': 'Add zone',
  'park.addZoneSub': 'Capacity is checked for every allocation, even when two desks allocate at once.',
  'park.addSpace': 'Add space',
  'park.exactOn': 'Exact spaces may be allocated.',
  'park.exactOff': 'Parking is allocated by zone.',
  'park.allocations': 'Current allocations',
  'park.allocationsSub': 'Vehicles with parking during the next hour.',
  'park.allocate': 'Allocate parking',
  'park.reallocate': 'Change allocation',
  'park.change': 'Change parking',
  'park.chooseZone': 'Choose a zone…',
  'park.zoneOption': '{name} ({free} free)',
  'park.anySpace': 'Any space in the zone',
  'park.validity': 'Valid',
  'park.assistance': 'Assistance',
  'park.accessibleAllocated': 'Accessible parking is allocated.',
  'pass.parking.title': 'Parking',
  'pass.parking.sub': 'Your parking for this visit. It does not open any gate by itself.',

  // ------------------------------------------------------------ gate queue
  'gate.m.expected': 'Expected vehicles',
  'gate.m.expectedSub': 'Due today',
  'gate.m.onsite': 'Vehicles onsite',
  'gate.m.onsiteSub': 'Observed, not inferred',
  'gate.m.exceptions': 'Plate exceptions',
  'gate.m.exceptionsSub': 'Need a guard',
  'gate.m.tasks': 'Open gate tasks',
  'gate.m.tasksSub': 'Escalations and follow-ups',
  'gate.attention': 'Needs attention',
  'gate.attentionSub': 'Blockers, unresolved plate reads and open tasks first.',
  'gate.blockedExpected': 'Expected, but currently blocked · host {host}',
  'gate.expected': 'Expected vehicles',
  'gate.expectedSub': 'Manual review before any observed movement.',
  'gate.expectedNone': 'No vehicles expected today.',
  'gate.onsite': 'Vehicles onsite',
  'gate.onsiteSub': 'Vehicle presence is recorded separately from each driver’s check-in and checkout.',
  'gate.onsiteNone': 'No vehicle recorded as onsite.',
  'gate.onsiteSince': 'Since {time}',
  'gate.overstay': 'Past its window',
  'gate.driverPresence': 'Driver: {state}',
  'gate.reads': 'Recent plate reads',
  'gate.readsSub': 'Camera reads locate candidates only.',
  'gate.commands': 'Barrier commands',
  'gate.commandsSub': 'Adapter outcomes. An acknowledgement does not prove anyone crossed.',
  'gate.col.vehicle': 'Vehicle',
  'gate.col.driver': 'Driver',
  'gate.col.visit': 'Linked visit',
  'gate.col.presence': 'Presence',
  'gate.col.action': 'Action',
  'gate.hint.noBlocker': 'No blocker found yet',
  'gate.hint.blocked': 'Blocked',
  'gate.review': 'Gate review',
  'gate.recordExit': 'Record exit',
  'gate.lookup': 'Find a vehicle or visitor',
  'gate.lookupHint': 'Plate, visit reference (V-…) or pass QR',
  'gate.lookupGo': 'Find',
  'gate.anyJurisdiction': 'Any jurisdiction',
  'gate.lookupNone': 'No expected vehicle or visitor matches.',
  'gate.lookupNotExpected': 'This plate is registered but not expected now. A registered plate is not permission.',
  'gate.lookupDead': 'This pass link is no longer valid.',
  'gate.nearMatch': 'Similar plate — check carefully',

  // ------------------------------------------------------------ review
  'gate.reviewSub': 'Minimal facts for this gate. The server checks them again when you act.',
  'gate.outcome.clear': 'Clear to permit',
  'gate.outcome.blocked': 'Blocked',
  'gate.outcome.unresolved': 'Unresolved — not clear',
  'gate.direction': 'Direction',
  'gate.direction.entry': 'Entry',
  'gate.direction.exit': 'Exit',
  'gate.reviewValid': 'Review valid for {seconds} s.',
  'gate.reviewExpired': 'This review has expired. Refresh before acting.',
  'gate.refresh': 'Refresh',
  'gate.matchNotPermission': 'A recognised vehicle never means permission to enter.',
  'gate.staleNoPermit': 'Data is stale or the connection is lost. Entry cannot be permitted from old facts.',
  'gate.person': 'Person',
  'gate.noVisit': 'No linked visit',
  'gate.hostedBy': 'Host: {host}',
  'gate.window': 'Window',
  'gate.destination': 'Destination',
  'gate.vehiclePresence': 'Vehicle presence',
  'gate.facts': 'Decision facts',
  'gate.factKey.approval': 'Approval',
  'gate.factKey.host_confirmation': 'Host confirmation',
  'gate.factKey.window': 'Visit window',
  'gate.factKey.identity': 'Identity',
  'gate.factKey.credential': 'Logical credential',
  'gate.factKey.permit': 'Permit',
  'gate.factKey.screening': 'Screening',
  'gate.factKey.zone': 'Requested area',
  'gate.factKey.parking': 'Parking',
  'gate.factKey.plate_read': 'Plate read',
  'gate.factKey.vehicle_presence': 'Vehicle presence',
  'gate.factKey.vehicle_status': 'Vehicle registry',
  'gate.factState.ok': 'OK',
  'gate.factState.blocked': 'Blocked',
  'gate.factState.unknown': 'Unknown',
  'gate.factState.info': 'Note',
  'gate.factState.not_available': 'Not available',
  'gate.source.passage': 'Passage',
  'gate.source.anpr': 'Plate camera',
  'gate.source.module_pending': 'Not connected yet',
  'gate.source.policy': 'Site policy',
  'gate.fact.window.open': 'Window open',
  'gate.fact.window.early': 'Early, within tolerance',
  'gate.fact.window.not_open': 'Window not open yet',
  'gate.fact.window.ended': 'Window ended',
  'gate.fact.hostConfirmed': 'Host confirmed',
  'gate.fact.hostNotConfirmed': 'Host has not confirmed',
  'gate.fact.identityVerified': 'Identity meets this visitor type’s requirement',
  'gate.fact.identityNotRequired': 'No identity check required for this visitor type',
  'gate.fact.screeningNotApplicable': 'Screening does not apply to this visitor type',
  'gate.fact.screening.clear': 'Screening clear',
  'gate.fact.screening.review': 'Security review required — send to the security supervisor',
  'gate.fact.screening.confirmed': 'Security hold — do not admit; call the security supervisor',
  'gate.fact.screening.unavailable': 'Screening unavailable — follow the site procedure',
  'gate.blocker.identity_required': 'This visitor type needs verified identity before entry.',
  'gate.blocker.security_hold': 'Security hold: do not admit. Call the security supervisor.',
  'gate.unknown.security_review': 'A security review is pending.',
  'gate.unknown.screening_unavailable': 'Screening could not be completed.',
  'gate.warning.screening_unavailable': 'Screening could not be completed; this site lets entry continue with care.',
  'gate.fact.identityNotVerified': 'Identity not verified in Passage (module not connected)',
  'gate.fact.contractorEligible': 'Contractor checks met (company, worker evidence, permit)',
  'gate.fact.contractorNotEligible': 'Contractor checks not met — see the contractor desk',
  'gate.fact.contractorNotApplicable': 'No contractor checks for this visit type',
  'gate.blocker.reentry_not_allowed': 'This person has already checked out; the policy does not allow re-entry on the same visit.',
  'gate.blocker.contractor_not_eligible': 'Contractor checks are not met (company, evidence or permit).',
  'gate.fact.permitNotConnected': 'Permit checks are not connected yet',
  'gate.fact.screeningNotConfigured': 'Screening is not configured',
  'gate.fact.zoneOrdinary': 'Ordinary area',
  'gate.fact.zoneRestricted': 'Restricted area — follow the site procedure',
  'gate.fact.parkingAllocated': 'Parking allocated',
  'gate.fact.parkingNone': 'No parking allocated',
  'gate.fact.readStale': 'Read too old to rely on',
  'gate.read.matched': 'Single match',
  'gate.read.ambiguous': 'Ambiguous',
  'gate.read.no_match': 'Not registered',
  'gate.read.low_confidence': 'Low confidence',
  'gate.read.unreadable': 'Unreadable',
  'gate.read.duplicate': 'repeat',
  'gate.read.noText': 'No readable text',
  'gate.blocker.no_linked_visit': 'Not expected: no visit is linked now.',
  'gate.blocker.visit_not_submitted': 'The visit is not submitted.',
  'gate.blocker.visit_cancelled': 'The visit is cancelled.',
  'gate.blocker.visit_ended': 'The visit has ended.',
  'gate.blocker.participant_removed': 'The visitor was removed from the visit.',
  'gate.blocker.approval_pending': 'Approval is still pending.',
  'gate.blocker.approval_rejected': 'The visit was not approved.',
  'gate.blocker.host_confirmation_pending': 'The host has not confirmed this unscheduled visit.',
  'gate.blocker.window_not_open': 'The visit window has not opened (an expected plate for another day authorises nothing today).',
  'gate.blocker.window_ended': 'The visit window has ended.',
  'gate.blocker.vehicle_blocked': 'This vehicle is blocked in the registry.',
  'gate.blocker.vehicle_inactive': 'This vehicle is inactive in the registry.',
  'gate.blocker.vehicle_already_onsite': 'This vehicle is already recorded as onsite.',
  'gate.unknown.plate_match_ambiguous': 'The plate read matches more than one vehicle.',
  'gate.unknown.plate_match_low_confidence': 'The plate read has low confidence.',
  'gate.unknown.plate_unreadable': 'The plate could not be read.',
  'gate.unknown.plate_read_stale': 'The plate read is too old to rely on.',
  'gate.unknown.vehicle_presence_conflicting': 'Vehicle presence is conflicting.',
  'gate.warning.early': 'Arriving early, within the site’s tolerance.',
  'gate.warning.identity_evidence_missing': 'Identity evidence is not verified in Passage.',
  'gate.warning.restricted_destination': 'The destination is a restricted area.',
  'gate.warning.parking_not_allocated': 'No parking allocated.',
  'gate.warning.exit_without_entry': 'No entry was recorded for this vehicle.',

  // ------------------------------------------------------------ actions
  'gate.permit': 'Permit entry',
  'gate.permitted': 'Permitted entry recorded. Record the observed entry when the vehicle crosses.',
  'gate.recordEntry': 'Record observed entry',
  'gate.queueEntry': 'Queue observed entry (offline)',
  'gate.queueExit': 'Queue observed exit (offline)',
  'gate.entryRecorded': 'Observed entry recorded.',
  'gate.exitRecorded': 'Observed exit recorded.',
  'gate.taskOpened.unpermitted_entry': 'No permitted-entry decision was recorded; a follow-up task was opened.',
  'gate.taskOpened.offline_reconcile': 'Recorded offline without a current decision; queued for supervisor review.',
  'gate.taskOpened.exit_without_entry': 'No entry was recorded; a follow-up task was opened.',
  'gate.observedEntry': 'Observed entry',
  'gate.observedExit': 'Observed exit',
  'gate.deny': 'Deny entry',
  'gate.denied': 'Denial recorded. Give the visitor-safe message and next step.',
  'gate.deny.reason': 'Structured reason',
  'gate.deny.next': 'Permitted next step',
  'gate.deny.note': 'Note',
  'gate.deny.noteHint': 'Visible to gate staff. No screening details.',
  'gate.deny.restricted': 'Restricted note',
  'gate.deny.restrictedHint': 'Readable only by security supervisors.',
  'gate.deny.visitorSafe': 'The visitor sees a neutral message; it never reveals screening or watchlist status.',
  'gate.deny.submit': 'Record denial',
  'gate.reason.not_expected': 'Not expected',
  'gate.reason.outside_window': 'Outside the visit window',
  'gate.reason.approval_pending': 'Approval pending',
  'gate.reason.approval_rejected': 'Not approved',
  'gate.reason.visit_cancelled': 'Visit cancelled',
  'gate.reason.vehicle_not_registered': 'Vehicle not registered',
  'gate.reason.vehicle_blocked': 'Vehicle blocked',
  'gate.reason.identity_unverified': 'Identity could not be verified',
  'gate.reason.parking_full': 'No parking available',
  'gate.reason.host_unavailable': 'Host unavailable',
  'gate.reason.screening_hold': 'Screening hold',
  'gate.reason.safety': 'Safety',
  'gate.reason.other': 'Other',
  'gate.reason.restricted': 'Restricted reason',
  'gate.visitorMessage.contact_host': 'Please contact your host.',
  'gate.visitorMessage.outside_window': 'Your visit is not scheduled for now.',
  'gate.visitorMessage.awaiting_approval': 'Your visit is awaiting approval.',
  'gate.visitorMessage.vehicle_not_registered': 'This vehicle is not registered for your visit.',
  'gate.visitorMessage.identity_check': 'We need to complete an identity check.',
  'gate.visitorMessage.parking_full': 'Parking is not available right now.',
  'gate.visitorMessage.cannot_proceed': 'Entry cannot proceed at the moment.',
  'gate.visitorMessageLabel': 'Visitor told: {message}',
  'gate.restrictedNoteLabel': 'Restricted note: {note}',
  'gate.next.contact_host': 'Contact the host',
  'gate.next.return_later': 'Return within the visit window',
  'gate.next.visit_reception': 'Go to reception',
  'gate.next.register_vehicle': 'Register the vehicle',
  'gate.next.leave_site': 'Leave the site',
  'gate.next.supervisor_review': 'Supervisor review',
  'gate.decision.permit': 'Entry permitted',
  'gate.decision.deny': 'Entry denied',
  'gate.escalate': 'Contact host / escalate',
  'gate.escalation.hint': 'Why the request is unresolved.',
  'gate.escalation.notify': 'Send the host a notice',
  'gate.escalation.notApproval': 'Escalating never approves the visit; approval stays with the approvers.',
  'gate.escalation.noSubject': 'Escalation needs a linked visit or visitor.',
  'gate.escalation.opened': 'Escalation opened.',
  'gate.escalation.repeated': 'Added to the open escalation.',
  'gate.task.host_contact': 'Host contact',
  'gate.task.supervisor_escalation': 'Supervisor escalation',
  'gate.task.unpermitted_entry': 'Entry observed without a permitted-entry decision',
  'gate.task.exit_without_entry': 'Exit observed without a recorded entry',
  'gate.task.vehicle_overstay': 'Vehicle past its window',
  'gate.task.vehicle_left_behind': 'Driver left; vehicle still onsite',
  'gate.task.barrier_unknown': 'Barrier outcome unknown',
  'gate.task.photo_declined': 'Photo not taken — assisted process',
  'gate.task.offline_reconcile': 'Offline observation to reconcile',
  'gate.task.other': 'Gate task',
  'gate.task.owner': 'Waiting on {name}',
  'gate.task.repeats': { one: 'Escalated again once', other: 'Escalated again {count} times' },
  'gate.task.resolution': 'Resolution',
  'gate.task.resolve': 'Resolve',
  'gate.taskEvent.opened': 'Opened',
  'gate.taskEvent.repeated': 'Escalated again',
  'gate.taskEvent.owner_changed': 'Owner changed',
  'gate.taskEvent.resolved': 'Resolved',
  'gate.history': 'History',
  'gate.movement.entry': 'Entry observed',
  'gate.movement.exit': 'Exit observed',
  'gate.movement.void': 'Withdraw',
  'gate.movement.voided': 'withdrawn',
  'gate.movement.correct': 'Correct',
  'gate.method.guard_visual': 'Seen by guard',
  'gate.method.anpr_confirmed': 'Plate camera, confirmed by guard',
  'gate.method.manual_plate': 'Plate typed by guard',
  'gate.method.offline_reconciled': 'Recorded offline',
  'gate.method.correction': 'Correction',

  // ------------------------------------------------------------ plate exceptions
  'gate.exception.ambiguous': 'Ambiguous plate read',
  'gate.exception.low_confidence': 'Low-confidence plate read',
  'gate.exception.unreadable': 'Unreadable plate',
  'gate.exception.no_match': 'Unregistered plate',
  'gate.exception.sub': 'Resolve the read by hand. The observed read is kept unchanged.',
  'gate.exception.never': 'An unresolved or ambiguous read never opens a gate.',
  'gate.exception.observed': 'Observed read',
  'gate.exception.confidence': 'Confidence',
  'gate.exception.noConfidence': 'Not reported',
  'gate.exception.candidates': 'Possible vehicles (masked)',
  'gate.exception.expectedToday': 'Expected today',
  'gate.exception.notExpected': 'Not expected today',
  'gate.exception.corrected': 'Corrected plate',
  'gate.exception.correctedHint': 'Only an exact registered plate is matched.',
  'gate.exception.match': 'Match',
  'gate.exception.dismiss': 'Dismiss',
  'gate.exception.resolve': 'Resolve',

  // ------------------------------------------------------------ assisted registration
  'gate.register.open': 'Register arrival',
  'gate.register.title': 'Assisted arrival registration',
  'gate.register.sub': 'Minimal details only. The visit goes through the normal policy, approval and host confirmation.',
  'gate.register.givenName': 'Given name',
  'gate.register.familyName': 'Family name',
  'gate.register.company': 'Company',
  'gate.register.phone': 'Mobile',
  'gate.register.host': 'Host',
  'gate.register.hostHint': 'Type at least 3 letters of the host’s name. Lookups are limited.',
  'gate.register.findHost': 'Find host',
  'gate.register.noHost': 'No host of this site matches.',
  'gate.register.category': 'Visit type',
  'gate.register.purpose': 'Purpose',
  'gate.register.note': 'The host is notified. The visitor waits until every check passes.',
  'gate.register.submit': 'Register and contact host',

  // ------------------------------------------------------------ photos
  'gate.photo.open': 'Photo',
  'gate.photo.notice': 'Explain why the photo is taken and that it is kept for {days} days. Never take a photo without telling the person.',
  'gate.photo.noticeGiven': 'I gave the purpose notice',
  'gate.photo.purpose': 'Purpose',
  'gate.photo.purpose.vehicle_condition': 'Vehicle condition',
  'gate.photo.purpose.plate_evidence': 'Plate evidence',
  'gate.photo.purpose.visitor_identification': 'Visitor identification',
  'gate.photo.camera': 'Camera',
  'gate.photo.preview': 'Photo preview',
  'gate.photo.save': 'Save photo',
  'gate.photo.retake': 'Retake',
  'gate.photo.saved': 'Photo saved. It is kept for {days} days and every view is recorded.',
  'gate.photo.cameraUnavailable': 'Camera unavailable',
  'gate.photo.declined': 'Person declined',
  'gate.photo.assisted': 'Recorded. Follow the site’s assisted process instead of a photo.',

  // ------------------------------------------------------------ barrier
  'gate.barrier.open': 'Barrier',
  'gate.barrier.note': 'Sends one opening request through the gate adapter. It is never sent automatically; the outcome may be unknown and must then be checked.',
  'gate.barrier.submit': 'Request one opening',
  'gate.barrier.command': 'Barrier command at {gate}',
  'gate.command.queued': 'Queued',
  'gate.command.dispatched': 'Sent to adapter',
  'gate.command.acknowledged': 'Acknowledged by adapter',
  'gate.command.confirmed': 'Confirmed',
  'gate.command.failed': 'Failed',
  'gate.command.unknown': 'Outcome unknown',
  'gate.command.reconciling': 'Being checked',
  'gate.command.outcome': 'Checked outcome',
  'gate.command.reconcile': 'Record checked outcome',

  // ------------------------------------------------------------ offline (PB-10-15)
  'gate.offline.banner': 'Offline or stale data',
  'gate.offline.age': { zero: 'Data is less than a minute old.', one: 'Data is 1 minute old.', other: 'Data is {count} minutes old.' },
  'gate.offline.noData': 'No current data.',
  'gate.offline.noProcedure': 'No offline procedure is approved for this site: contact your supervisor.',
  'gate.offline.procedureAvailable': 'Follow the approved offline procedure. Stale data is never current permission.',
  'gate.offline.title': 'Offline procedure',
  'gate.offline.sub': 'What to do when Passage cannot be reached.',
  'gate.offline.rule': 'Stale or cached data is never current permission. Entry decisions need a fresh review.',
  'gate.offline.connection': 'Connection',
  'gate.offline.online': 'Online',
  'gate.offline.offline': 'Offline',
  'gate.offline.dataAge': 'Data age',
  'gate.offline.procedure': 'Approved procedure',
  'gate.offline.noProcedureLong': 'This site has no approved offline procedure. Do not admit anyone from cached data; contact your supervisor.',
  'gate.offline.queue': 'Observations waiting to sync',
  'gate.offline.queueEmpty': 'Nothing waiting.',
  'gate.offline.sync': 'Sync now',
  'gate.offline.queued': 'Observation stored on this device. Sync it when the connection returns.',
  'gate.offline.notStored': 'This device could not store the observation. Record it by the approved manual procedure.',

  // ------------------------------------------------------------ gate policy (PD-162)
  'gpol.title': 'Gate settings',
  'gpol.sub': 'Site policy values. Defaults are development defaults, not approved operational rules.',
  'gpol.early': 'Early arrival tolerance (minutes)',
  'gpol.maxAge': 'Review validity (seconds)',
  'gpol.maxAgeHint': 'An older review cannot be acted on.',
  'gpol.minConfidence': 'Minimum plate-read confidence (0.5–1)',
  'gpol.minConfidenceHint': 'Lower reads go to a guard.',
  'gpol.grace': 'Overstay grace (minutes)',
  'gpol.exactSpaces': 'Allow exact parking spaces',
  'gpol.notify': 'Send visitors a notice when parking changes',
  'gpol.photo': 'Photo capture',
  'gpol.photoEnabled': 'Allow photo capture for the purposes below',
  'gpol.retention': 'Keep photos for (days)',
  'gpol.offline': 'Offline procedure',
  'gpol.offlineApproved': 'An offline procedure is approved for this site',
  'gpol.procedure': 'Procedure shown to guards',
  'gpol.dataAge': 'Data counts as stale after (minutes)',
  'gpol.barrier': 'Barrier adapter',
  'gpol.barrierHint': 'No real barrier integration exists yet.',
  'gpol.barrier.none': 'None',
  'gpol.barrier.simulator': 'Simulator (development only)',
  'gpol.jurisdictions': 'Plate jurisdictions',
  'gpol.jurisdictionsHint': 'Comma-separated codes, for example AE-DU, AE-AZ, SA, XX.',
} as const;
